'use client';

import {
  Children,
  isValidElement,
  useId,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

export interface TabsProps {
  defaultIndex?: number;
  activeIndex?: number; // controlled mode: caller owns the active index
  onChange?: (index: number) => void; // controlled mode: called on tab click or keyboard
  orientation?: 'horizontal' | 'vertical';
  /**
   * When true, the panel wrapper and the active panel carry `min-h-0` / `h-full`
   * so a full-height child can establish its own bounded scroll frame inside the
   * tab panel. Off by default: normal content tabs must stay auto-height (their
   * children set their own height). Pass a bounded-height `className`
   * (e.g. `flex h-full min-h-0 flex-col overflow-hidden`) alongside it so the
   * chain actually resolves. Needed by the catalog item-editor workbench, whose
   * only-middle-column-scrolls layout (#6216) breaks when the form is wrapped in
   * Tabs for the pricing tab (#6269).
   */
  fitHeight?: boolean;
  /**
   * Visual weight of the tab strip. Default 'sm' reproduces today's classes
   * exactly for the many existing consumers. 'md' is opt-in: larger label,
   * more padding, a heavier 3px active rule, and font-medium on the active
   * tab — for the rare page where the tab switcher IS primary navigation
   * (e.g. the catalog item editor's Edit/Pricing switch) and needs to
   * compete visually with a title-adjacent action row, not just categorize
   * content beneath it.
   */
  size?: 'sm' | 'md';
  /** Floating individual pills, or the existing underline strip (default). */
  variant?: 'underline' | 'pills';
  /**
   * Horizontal underline strip only. When the labels are wider than the
   * container the strip scrolls sideways (labels never wrap or shrink) instead
   * of pushing the page wider. Off by default: nothing changes for existing
   * consumers. Focus rings go inset in this mode, because a scrolling box would
   * clip the default outset ring.
   */
  scrollable?: boolean;
  children: ReactNode;
  className?: string;
}

interface TabProps {
  label: ReactNode;
  disabled?: boolean;
  children: ReactNode;
}

export function Tab(_: TabProps): null {
  return null; /* sentinel; real rendering in <Tabs> */
}

// raw-button-ok-file: this file's <button> is the Tabs primitive's own
// tab-trigger control, not a page action — no <Button> conversion applies. (2026-07-09)

export function Tabs({
  defaultIndex = 0,
  activeIndex,
  onChange,
  orientation = 'horizontal',
  fitHeight = false,
  size = 'sm',
  variant = 'underline',
  scrollable = false,
  children,
  className,
}: TabsProps) {
  const baseId = useId();
  const isControlled = activeIndex !== undefined;
  const [internalActive, setInternalActive] = useState(defaultIndex);
  const active = isControlled ? activeIndex : internalActive;

  function setActive(i: number) {
    if (!isControlled) setInternalActive(i);
    onChange?.(i);
  }

  const tabs = Children.toArray(children).filter(isValidElement) as ReactElement<TabProps>[];

  const layout = orientation === 'vertical' ? 'flex flex-row gap-4' : 'flex flex-col gap-3';
  const pills = variant === 'pills';
  const scrolls = scrollable && !pills && orientation === 'horizontal';
  const listLayout = pills
    ? orientation === 'vertical'
      ? 'flex flex-col items-start gap-2'
      : 'flex flex-row flex-wrap gap-2'
    : orientation === 'vertical'
      ? 'flex flex-col border-r border-border min-w-32'
      : scrolls
        ? 'flex flex-row overflow-x-auto border-b border-border'
        : 'flex flex-row border-b border-border';
  // `sm` pills drop the vertical padding: their box is a fixed 30px (ADR-0192 D4).
  const tabPad =
    size === 'md'
      ? 'px-5 py-2.5 text-body'
      : pills
        ? 'px-4 text-body-sm'
        : 'px-4 py-2 text-body-sm';
  const tabBorder = size === 'md' ? 'border-b-[3px]' : 'border-b-2';

  function onKey(e: React.KeyboardEvent) {
    const lastIdx = tabs.length - 1;
    const isForward =
      (orientation === 'horizontal' && e.key === 'ArrowRight') ||
      (orientation === 'vertical' && e.key === 'ArrowDown');
    const isBackward =
      (orientation === 'horizontal' && e.key === 'ArrowLeft') ||
      (orientation === 'vertical' && e.key === 'ArrowUp');
    if (!isForward && !isBackward) return;
    e.preventDefault();
    let next = active;
    if (isForward) {
      for (let i = active + 1; i <= lastIdx; i++) {
        if (!tabs[i]?.props.disabled) {
          next = i;
          break;
        }
      }
    } else {
      for (let i = active - 1; i >= 0; i--) {
        if (!tabs[i]?.props.disabled) {
          next = i;
          break;
        }
      }
    }
    if (next !== active) {
      setActive(next);
      document.getElementById(`${baseId}-tab-${next}`)?.focus();
    }
  }

  return (
    <div className={[layout, className].filter(Boolean).join(' ')}>
      <div role="tablist" aria-orientation={orientation} className={listLayout} onKeyDown={onKey}>
        {tabs.map((t, i) => {
          const id = `${baseId}-tab-${i}`;
          const panelId = `${baseId}-panel-${i}`;
          return (
            <button
              key={i}
              id={id}
              role="tab"
              type="button"
              disabled={t.props.disabled}
              aria-selected={i === active}
              aria-controls={panelId}
              tabIndex={i === active ? 0 : -1}
              onClick={() => !t.props.disabled && setActive(i)}
              className={[
                tabPad,
                // ADR-0192 D4 + Q8: `sm` pills are a fixed 30px box (1px border + a
                // 20px line, centered). `md` pills keep `min-h-11`; underline tabs
                // are untouched.
                pills
                  ? size === 'md'
                    ? 'min-h-11 rounded-full border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2'
                    : 'inline-flex items-center h-[var(--pill-tab-height)] rounded-full border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2'
                  : scrolls
                    ? `${tabBorder} shrink-0 whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring-focus`
                    : `${tabBorder} focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-1`,
                // Complete static strings per branch -- a utility written
                // immediately before `${` is read by Tailwind as one candidate
                // and never emitted (see
                // scripts/check-tailwind-class-interpolation.cjs).
                pills
                  ? i === active
                    ? 'border-selected bg-selected-pale text-selected-text'
                    : 'border-border bg-transparent text-secondary hover:bg-surface-elevated'
                  : i === active
                    ? size === 'md'
                      ? 'border-selected text-selected-text font-medium'
                      : 'border-selected text-selected-text'
                    : 'border-transparent text-secondary',
                t.props.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:text-primary',
              ].join(' ')}
            >
              {t.props.label}
            </button>
          );
        })}
      </div>
      <div className={['flex-1', fitHeight ? 'min-h-0' : ''].filter(Boolean).join(' ')}>
        {tabs.map((t, i) => (
          <div
            key={i}
            id={`${baseId}-panel-${i}`}
            role="tabpanel"
            aria-labelledby={`${baseId}-tab-${i}`}
            hidden={i !== active}
            className={fitHeight ? 'h-full min-h-0' : undefined}
          >
            {i === active && t.props.children}
          </div>
        ))}
      </div>
    </div>
  );
}
